import { useEffect, useState, useCallback } from "react";
import Topbar from "@/components/Topbar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { apiClient } from "@/lib/api";
import { toast } from "sonner";
import PerfSaisie from "./PerfSaisie";
import PerfCatalogue from "./PerfCatalogue";
import PerfThresholds from "./PerfThresholds";

const todayISO = () => new Date().toISOString().slice(0, 10);

export default function PerfAdmin() {
  const [services, setServices] = useState([]);
  const [metrics, setMetrics] = useState([]);
  const [collaborators, setCollaborators] = useState([]);
  const [targets, setTargets] = useState([]);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    try {
      const [s, m, c, t, a] = await Promise.all([
        apiClient.get("/performance/services", { params: { include_inactive: true } }),
        apiClient.get("/performance/metrics", { params: { include_inactive: true } }),
        apiClient.get("/performance/collaborators", { params: { include_inactive: true } }),
        apiClient.get("/performance/targets", { params: { on: todayISO() } }),
        apiClient.get("/performance/audit", { params: { limit: 60 } }),
      ]);
      setServices(s.data);
      setMetrics(m.data);
      setCollaborators(c.data);
      setTargets(t.data);
      setAudit(a.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de chargement du module Performance");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const activeServices = services.filter((s) => s.active);
  const activeMetrics = metrics.filter((m) => m.active && activeServices.some((s) => s.id === m.service_id));

  return (
    <>
      <Topbar title="Administration Performance" subtitle="Saisie journalière · indicateurs · objectifs — réservé aux administrateurs" />
      <main className="px-4 sm:px-6 lg:px-8 py-5 sm:py-8 max-w-[1600px] w-full" data-testid="perf-admin">
        <Tabs defaultValue="saisie">
          <TabsList className="bg-zinc-100 rounded-none p-1 h-auto flex-wrap justify-start mb-6" data-testid="perf-admin-tabs">
            <TabsTrigger value="saisie" className="rounded-sm data-[state=active]:bg-zinc-900 data-[state=active]:text-white text-xs sm:text-sm" data-testid="perf-admin-tab-saisie">Saisie journalière</TabsTrigger>
            <TabsTrigger value="catalogue" className="rounded-sm data-[state=active]:bg-zinc-900 data-[state=active]:text-white text-xs sm:text-sm" data-testid="perf-admin-tab-catalogue">Indicateurs & services</TabsTrigger>
            <TabsTrigger value="seuils" className="rounded-sm data-[state=active]:bg-zinc-900 data-[state=active]:text-white text-xs sm:text-sm" data-testid="perf-admin-tab-seuils">Seuils de couleur</TabsTrigger>
            <TabsTrigger value="journal" className="rounded-sm data-[state=active]:bg-zinc-900 data-[state=active]:text-white text-xs sm:text-sm" data-testid="perf-admin-tab-journal">Journal</TabsTrigger>
          </TabsList>

          {loading ? (
            <div className="bg-white border border-zinc-200 px-5 py-10 text-center text-sm text-zinc-500">Chargement…</div>
          ) : (
            <>
              <TabsContent value="saisie">
                <PerfSaisie
                  services={activeServices}
                  metrics={activeMetrics}
                  collaborators={collaborators.filter((c) => c.active)}
                  onSaved={reload}
                />
              </TabsContent>

              <TabsContent value="catalogue">
                <PerfCatalogue
                  services={services}
                  metrics={metrics}
                  collaborators={collaborators}
                  targets={targets}
                  reload={reload}
                />
              </TabsContent>

              <TabsContent value="seuils">
                <PerfThresholds />
              </TabsContent>

              <TabsContent value="journal">
                <div className="bg-white border border-zinc-200 overflow-x-auto" data-testid="perf-journal">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-zinc-200 bg-zinc-50">
                        <Th>Date</Th><Th>Utilisateur</Th><Th>Action</Th><Th>Détail</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {audit.length === 0 && (
                        <tr><td colSpan={4} className="px-4 py-8 text-center text-zinc-500">Aucune action enregistrée pour l'instant.</td></tr>
                      )}
                      {audit.map((r) => (
                        <tr key={r.id} className="border-b border-zinc-100 last:border-0">
                          <td className="px-4 py-3 font-mono text-xs text-zinc-500 whitespace-nowrap">
                            {new Date(r.at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}
                          </td>
                          <td className="px-4 py-3">{r.user_name || "—"}</td>
                          <td className="px-4 py-3 font-medium">{r.action}</td>
                          <td className="px-4 py-3 text-zinc-500">{r.detail}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </TabsContent>
            </>
          )}
        </Tabs>
      </main>
    </>
  );
}

const Th = ({ children }) => (
  <th className="label-tiny text-zinc-400 font-medium px-4 py-3 text-left">{children}</th>
);
